'use client'

import { useAuth } from '@clerk/nextjs'
import { usePathname } from 'next/navigation'
import { useCallback, useState } from 'react'
import { useBrandContext } from '@/contexts/brand-context'
import { useOrgContext } from '@/contexts/org-context'
import { api } from '@/convex/_generated/api'
import { useAuthedQuery } from '@/hooks/use-authed-query'
import {
  getIntegrationTypeLabel,
  type IntegrationProvider,
  type IntegrationType,
  integrationData,
} from '@/lib/integrations/catalog'
import { projectProviderSetupInput } from '@/lib/integrations/contracts/setup-schemas'
import { buildFigmaConnectPath } from '@/lib/integrations/oauth/figma/connect-path'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { appToast } from '@/lib/shared/toast'
import type { ProviderSettings } from '../config/provider-configs'

function getProviderType(
  provider: IntegrationProvider
): IntegrationType | undefined {
  return integrationData.find((meta) => meta.id === provider)?.integrationType
}

export function useIntegration({
  provider,
  name,
}: {
  provider: IntegrationProvider
  name: string
}) {
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [disconnectedAt, setDisconnectedAt] = useState<number | null>(null)
  const { userId } = useAuth()
  const { orgId } = useOrgContext()
  const { brandId } = useBrandContext()
  const pathname = usePathname()

  const integration = useAuthedQuery(
    api.integrations.queries.getByProvider,
    orgId && brandId ? { orgId, brandId, provider } : 'skip'
  )

  const providerType = getProviderType(provider)
  const isApiKeyProvider =
    providerType !== undefined && providerType !== 'oauth'
  // Slack is OAuth in concept, but its install/auth flow lives entirely
  // inside the SlackForm component (Slack App install URL), not the
  // generic OAuth flow this hook drives. Keep it out of `isOAuthProvider`
  // so the dialog renders SlackForm instead of ConnectButton.
  const isOAuthProvider = providerType === 'oauth' && provider !== 'slack'

  const isServerConnected = integration?.status === 'active'
  const isPendingVerification = integration?.status === 'pending_verification'
  const serverUpdatedAt = integration?._creationTime ?? 0
  const isConnected =
    disconnectedAt && disconnectedAt > serverUpdatedAt
      ? false
      : isServerConnected
  const apiKeyLastFour =
    isConnected && isApiKeyProvider
      ? (integration?.apiKeyLastFour ?? undefined)
      : undefined
  const portalName =
    provider === 'hubspot'
      ? (integration?.externalWorkspaceName ?? undefined)
      : undefined

  let oAuthUrl: string | undefined
  if (provider === 'figma') {
    oAuthUrl = buildFigmaConnectPath(pathname || '/integrations')
  } else if (provider === 'hubspot') {
    oAuthUrl = `/api/integrations/hubspot/oauth/authorize?returnUrl=${encodeURIComponent(
      pathname || '/integrations'
    )}`
  } else if (provider === 'klaviyo') {
    oAuthUrl = `/api/integrations/klaviyo/oauth/authorize?returnUrl=${encodeURIComponent(
      pathname || '/integrations'
    )}`
  } else if (provider === 'mailchimp') {
    oAuthUrl = `/api/integrations/mailchimp/oauth/authorize?returnUrl=${encodeURIComponent(
      pathname || '/integrations'
    )}`
  } else if (provider === 'stripe') {
    oAuthUrl = `/api/integrations/stripe/oauth/authorize?returnUrl=${encodeURIComponent(
      pathname || '/integrations'
    )}`
  }

  const connect = useCallback(
    async (
      data: ProviderSettings
    ): Promise<'active' | 'pending_verification' | false> => {
      if (provider === 'slack') {
        return false
      }

      if (isOAuthProvider) {
        return false
      }

      if (!data.apiKey.trim()) {
        const credentialLabel = providerType
          ? getIntegrationTypeLabel(providerType).toLowerCase()
          : 'credential'
        appToast.error(`Please enter the ${credentialLabel}`)
        return false
      }

      const setupInput = projectProviderSetupInput(provider, data)
      if (!setupInput.success) {
        appToast.error('Check the integration settings and try again')
        return false
      }

      setIsConnecting(true)
      try {
        const response = await fetch(`/api/integrations/${provider}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(setupInput.data),
        })

        const result = (await response.json()) as {
          error?: string
          pendingVerification?: boolean
        }
        if (!response.ok) {
          throw new Error(result.error || 'Failed to connect')
        }

        if (result.pendingVerification) {
          appToast.success(`${name} setup saved`, {
            description:
              'Send a signed test event from the provider to finish verification.',
          })
          return 'pending_verification'
        }
        appToast.success(`${name} connected successfully!`)
        return 'active'
      } catch (error) {
        appToast.error(getErrorMessage(error, 'Failed to connect'))
        return false
      } finally {
        setIsConnecting(false)
      }
    },
    [provider, name, isOAuthProvider, providerType]
  )

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true)
    try {
      const response = await fetch(`/api/integrations/${provider}`, {
        method: 'DELETE',
      })
      const result = (await response.json()) as {
        error?: string
        message?: string
        manualRevokeUrl?: string
        warning?: {
          code: string
          message: string
          retryable: boolean
          helpUrl?: string
        }
        revokeAtFigmaUrl?: string
        revokeAtStripeUrl?: string
      }
      if (!response.ok) {
        throw new Error(result.error || 'Failed to disconnect')
      }

      setDisconnectedAt(Date.now())
      if (result.warning) {
        const manualRevokeUrl = result.manualRevokeUrl
        appToast.warning(`${name} disconnected; finish revocation`, {
          description: result.warning.message,
          ...(manualRevokeUrl
            ? {
                action: {
                  label: `Open ${name}`,
                  onClick: () => {
                    window.open(
                      manualRevokeUrl,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  },
                },
              }
            : {}),
        })
      } else if (provider === 'figma' && result.revokeAtFigmaUrl) {
        // Figma has no public revocation endpoint, so the disconnect
        // response includes a deep link to Figma's connected-apps page.
        // Surface it as a clickable toast action so users can finish
        // revocation in one click.
        appToast.success(`${name} disconnected`, {
          description:
            result.message ??
            "Remove Brew from your Figma 'Connected apps' settings to fully revoke access.",
          action: {
            label: 'Revoke at Figma',
            onClick: () => {
              window.open(
                result.revokeAtFigmaUrl,
                '_blank',
                'noopener,noreferrer'
              )
            },
          },
        })
      } else if (provider === 'stripe' && result.revokeAtStripeUrl) {
        // Stripe Apps OAuth v2 does not support programmatic
        // deauthorize either — see
        // https://github.com/stripe/stripe-apps/issues/837. Surface
        // the Stripe Dashboard apps page so the user can finish
        // uninstalling in one click. Until they do, Stripe keeps
        // delivering webhooks to Brew (which we mark `failed` since
        // the integration is gone), so the deep link isn't just UX
        // polish — it's the only way to stop the inbound traffic.
        appToast.success(`${name} disconnected`, {
          description:
            result.message ??
            'Remove Brew from your Stripe Dashboard\u2019s apps page to fully uninstall.',
          action: {
            label: 'Uninstall at Stripe',
            onClick: () => {
              window.open(
                result.revokeAtStripeUrl,
                '_blank',
                'noopener,noreferrer'
              )
            },
          },
        })
      } else {
        appToast.success(`${name} disconnected`)
      }
      return true
    } catch (error) {
      appToast.error(getErrorMessage(error, 'Failed to disconnect'))
      return false
    } finally {
      setIsDisconnecting(false)
    }
  }, [provider, name])

  return {
    isConnected,
    isPendingVerification,
    isConnecting,
    isDisconnecting,
    isApiKeyProvider,
    isOAuthProvider,
    apiKeyLastFour,
    portalName,
    oAuthUrl,
    userId: userId ?? undefined,
    integration,
    connect,
    disconnect,
  }
}
